import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { auth } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { TableProvider, TableContext } from '../../components/shared/table/context/TableContext';
import { EditModeProvider, EditModeContext } from '../../components/shared/table/context/EditModeContext';
import BaseTable from '../../components/shared/table/BaseTable';
import { useSystemAuctionLotsColumns, useSystemAuctionLotsRows } from './hooks/useSystemAuctionLotsColumns';
import SystemAuctionLotsToolbar from './components/SystemAuctionLotsToolbar';

// Modal components
const FormModal = ({ show, onClose, title, onSubmit, formData, handleFormDataChange }) => {
  if (!show) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={onSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {title}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Certification Number *</label>
                      <input
                        type="text"
                        required
                        value={formData.certification_number}
                        onChange={(e) => handleFormDataChange('certification_number', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Country</label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => handleFormDataChange('country', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Year</label>
                      <input
                        type="text"
                        value={formData.year}
                        onChange={(e) => handleFormDataChange('year', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Denomination</label>
                      <input
                        type="text"
                        value={formData.denomination}
                        onChange={(e) => handleFormDataChange('denomination', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Mint Mark</label>
                      <input
                        type="text"
                        value={formData.mint_mark}
                        onChange={(e) => handleFormDataChange('mint_mark', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Assayer</label>
                      <input
                        type="text"
                        value={formData.assayer}
                        onChange={(e) => handleFormDataChange('assayer', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Reference Number</label>
                      <input
                        type="text"
                        value={formData.reference_number}
                        onChange={(e) => handleFormDataChange('reference_number', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Variety</label>
                      <input
                        type="text"
                        value={formData.variety}
                        onChange={(e) => handleFormDataChange('variety', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Minor Variety</label>
                      <input
                        type="text"
                        value={formData.minor_variety}
                        onChange={(e) => handleFormDataChange('minor_variety', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Die Variety</label>
                      <input
                        type="text"
                        value={formData.die_variety}
                        onChange={(e) => handleFormDataChange('die_variety', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Metal</label>
                      <input
                        type="text"
                        value={formData.metal}
                        onChange={(e) => handleFormDataChange('metal', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Composition</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.composition}
                        onChange={(e) => handleFormDataChange('composition', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weight</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.weight}
                        onChange={(e) => handleFormDataChange('weight', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Diameter</label>
                      <input
                        type="number"
                        step="0.001"
                        value={formData.diameter}
                        onChange={(e) => handleFormDataChange('diameter', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Edge</label>
                      <input
                        type="text"
                        value={formData.edge}
                        onChange={(e) => handleFormDataChange('edge', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Mintage</label>
                      <input
                        type="text"
                        value={formData.mintage}
                        onChange={(e) => handleFormDataChange('mintage', e.target.value)}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Create
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DeleteModal = ({ show, onClose, onConfirm, selectedCount }) => {
  if (!show) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Delete System Auction Lot Records
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete {selectedCount} selected record{selectedCount > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inner component that has access to EditModeContext and TableContext
const TableContent = ({
  records,
  selectedRecords,
  onSetSelection,
  onRecordsUpdate,
  onAdd,
  onDeleteSelected
}) => {
  const { isEditMode } = useContext(EditModeContext);
  const { setColumns, filterRows, loading, error } = useContext(TableContext);
  const columns = useSystemAuctionLotsColumns();
  
  // Update columns in context when they change
  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);

  const processedRecords = useSystemAuctionLotsRows(records);
  const filteredRecords = filterRows(processedRecords);

  // Memoize selectedItems to prevent unnecessary re-renders and ensure string IDs
  const memoizedSelectedItems = useMemo(() => {
    return new Set([...selectedRecords].map(id => String(id)));
  }, [selectedRecords]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          Failed to load data: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none">
        <SystemAuctionLotsToolbar
          title="System Auction Lots Manager"
          columns={columns}
          data={processedRecords}
          selectedItems={memoizedSelectedItems}
          showQuickFilter={true}
          quickFilterProps={{ debounceMs: 300 }}
          onAdd={onAdd}
          onDelete={selectedRecords.size > 0 ? onDeleteSelected : null}
          onRefresh={onRecordsUpdate}
        />
      </div>
      <div className="flex-1 min-h-0">
        <BaseTable
          data={processedRecords}
          columns={columns}
          selectedItems={memoizedSelectedItems}
          onSelectionChange={onSetSelection}
          showThumbnails={false}
          renderCustomRow={null}
        />
      </div>
    </div>
  );
};

// Main component
const SystemAuctionLotsManager = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sort, setSort] = useState({ field: 'id', order: 'asc' });
  const [filters, setFilters] = useState({});
  const [formData, setFormData] = useState({
    certification_number: '',
    country: '',
    year: '',
    denomination: '',
    mint_mark: '',
    assayer: '',
    reference_number: '',
    variety: '',
    minor_variety: '',
    die_variety: '',
    metal: '',
    composition: '',
    weight: '',
    diameter: '',
    edge: '',
    mintage: '',
  });

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const data = await auth.getSystemAuctionLots();
      setRecords(data || []);
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setFormData({
      certification_number: '',
      country: '',
      year: '',
      denomination: '',
      mint_mark: '',
      assayer: '',
      reference_number: '',
      variety: '',
      minor_variety: '',
      die_variety: '',
      metal: '',
      composition: '',
      weight: '',
      diameter: '',
      edge: '',
      mintage: '',
    });
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleFormDataChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      
      // Convert numeric fields
      if (submitData.composition) submitData.composition = parseFloat(submitData.composition);
      if (submitData.weight) submitData.weight = parseFloat(submitData.weight);
      if (submitData.diameter) submitData.diameter = parseFloat(submitData.diameter);

      await auth.createSystemAuctionLot(submitData);

      setShowAddModal(false);
      setError(null);
      fetchRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSelected = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Delete all selected records
      for (const id of selectedRecords) {
        await auth.deleteSystemAuctionLot(id);
      }
      setShowDeleteModal(false);
      setSelectedRecords(new Set());
      setError(null);
      fetchRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  // Handler for saving edited cells
  const handleSaveChanges = useCallback(async (changes) => {
    try {
      console.log('Received changes:', changes);
      
      // Group changes by record ID
      const changesByRecord = changes.reduce((acc, change) => {
        const { id, field, value } = change;
        if (!acc[id]) {
          acc[id] = {};
        }
        
        // Convert empty strings to null and handle numeric fields
        let processedValue = value === '' ? null : value;
        
        if (field === 'composition' || field === 'weight' || field === 'diameter') {
          processedValue = processedValue ? parseFloat(processedValue) : null;
        }
        
        acc[id][field] = processedValue;
        return acc;
      }, {});

      // Update records sequentially
      for (const [id, updates] of Object.entries(changesByRecord)) {
        console.log(`Updating System Auction Lot record ${id} with:`, updates);
        await auth.updateSystemAuctionLot(id, updates);
      }

      console.log('All updates completed successfully');
      
      // Refresh data
      await fetchRecords();
      
      return true;
    } catch (error) {
      console.error('Failed to save changes:', error);
      setError(error.message);
      return false;
    }
  }, [fetchRecords]);

  return (
    <>
      <TableProvider
        type="system_auction_lots"
        initialState={{ loading, error }}
      >
        <EditModeProvider onSave={handleSaveChanges}>
          <TableContent
            records={records}
            selectedRecords={selectedRecords}
            onSetSelection={setSelectedRecords}
            onRecordsUpdate={fetchRecords}
            onAdd={handleAdd}
            onDeleteSelected={handleDeleteSelected}
          />
        </EditModeProvider>
      </TableProvider>

      {/* Modals */}
      <FormModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add System Auction Lot Record"
        onSubmit={handleSubmit}
        formData={formData}
        handleFormDataChange={handleFormDataChange}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        selectedCount={selectedRecords.size}
      />
    </>
  );
};

export default SystemAuctionLotsManager;
